export const normalizeEmailDomain = (domain: string): string =>
  domain.trim().toLowerCase().replace(/^@+/, "");

export const BOOKING_EMAIL_SEGMENTS = [
  "student",
  "employee",
  "unknown",
] as const;

export type BookingEmailSegment = (typeof BOOKING_EMAIL_SEGMENTS)[number];

export const UNKNOWN_BOOKING_EMAIL_SEGMENT =
  "unknown" satisfies BookingEmailSegment;

export type KnownBookingEmailSegment = Exclude<
  BookingEmailSegment,
  typeof UNKNOWN_BOOKING_EMAIL_SEGMENT
>;

const STUDENT_BOOKING_EMAIL_DOMAIN = "stud.th-rosenheim.de";
const EMPLOYEE_BOOKING_EMAIL_DOMAIN = "th-rosenheim.de";

export const inferBookingEmailSegmentFromDomain = (
  domain: string | undefined,
): BookingEmailSegment => {
  const normalizedDomain = domain ? normalizeEmailDomain(domain) : undefined;

  if (normalizedDomain === STUDENT_BOOKING_EMAIL_DOMAIN) {
    return "student";
  }

  if (normalizedDomain === EMPLOYEE_BOOKING_EMAIL_DOMAIN) {
    return "employee";
  }

  return UNKNOWN_BOOKING_EMAIL_SEGMENT;
};

export const getEmailDomain = (email: string): string | undefined => {
  const [, domain] = email.trim().toLowerCase().split("@");
  if (!domain) {
    return undefined;
  }

  return normalizeEmailDomain(domain);
};

export const isEmailDomainAllowed = (
  email: string,
  allowedDomains: string[],
): boolean => {
  const emailDomain = getEmailDomain(email);
  if (!emailDomain) {
    return false;
  }

  return allowedDomains.some((allowedDomain) => {
    return normalizeEmailDomain(allowedDomain) === emailDomain;
  });
};

export const inferBookingEmailSegment = (
  email: string,
): BookingEmailSegment => {
  return inferBookingEmailSegmentFromDomain(getEmailDomain(email));
};

export const stationRequiresLogin = (station: {
  requiresLogin: boolean;
  allowedEmailDomains: string[];
}): boolean => station.requiresLogin || station.allowedEmailDomains.length > 0;
